"use client";

import { memo, useState } from "react";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { PlaceChip, PlaceDidYouMean } from "@/components/PlaceChip";
import type { PlaceResult } from "@/lib/api/misc";
import type { KundaliLang, KundaliTranslations } from "./KundaliFormCard.i18n";
import {
  birthDateInlineError,
  formatBirthDateDisplayInput,
  normalizeBirthDateInput,
} from "./KundaliFormCard.validation";
import { KUNDALI_NAME_CWV_PROBE } from "./KundaliFormCard.cwv";

interface FieldWrapperProps {
  children: React.ReactNode;
}

export function FieldWrapper({ children }: FieldWrapperProps) {
  return <div>{children}</div>;
}

interface FieldHintProps {
  language: KundaliLang;
  en: string;
  hi: string;
}

export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p
      id={id}
      className="mt-1.5 text-caption font-medium"
      style={{ color: "var(--color-error-text)" }}
    >
      {message}
    </p>
  );
}

function FieldHint({ language, en, hi }: FieldHintProps) {
  return (
    <p className="text-[11px] mb-2" style={{ color: "var(--ish-text-muted)" }}>
      {language === "hi" ? hi : en}
    </p>
  );
}

// --- NameField ---

interface NameFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  language: KundaliLang;
  t: KundaliTranslations;
  error?: string;
}

export const NameField = memo(function NameField({
  value,
  onChange,
  onFocus,
  language,
  t,
  error,
}: NameFieldProps) {
  return (
    <FieldWrapper>
      <label
        htmlFor="name"
        className="block text-body-small font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.name}
      </label>
      {/* hi hints are native Hinglish (Roman) — the verified brand register
          (rule 21: single Indian noun + English action word). These were
          Devanagari while the same form's errors were Hinglish — a register
          split on the exact surface where the /hi completion gap concentrates. */}
      <FieldHint
        language={language}
        hi="Aapka naam reading ko personal banata hai"
        en="Your name helps our Jyotishi personalize your reading"
      />
      <input
        id="name"
        data-cwv-interaction-target={KUNDALI_NAME_CWV_PROBE.marker}
        type="text"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        placeholder={t.namePlaceholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "name-error" : undefined}
        className="input-sacred w-full rounded-lg px-4 py-3 text-body"
        style={{ color: "var(--ish-dark)" }}
      />
      <FieldError id="name-error" message={error} />
    </FieldWrapper>
  );
});

// --- BirthDateField ---

interface BirthDateFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  language: KundaliLang;
  t: KundaliTranslations;
  error?: string;
}

export const BirthDateField = memo(function BirthDateField({
  value,
  onChange,
  onFocus,
  language,
  t,
  error,
}: BirthDateFieldProps) {
  // Inline as-typed bound check (rejects pre-1900 / future / impossible dates the
  // moment a complete date is typed). The submit-time `error` still wins when
  // present; otherwise the inline message guides the seeker before they submit
  // — this is the lever against the 8,684 birthDate failures.
  const inlineError = birthDateInlineError(value, language);
  const shownError = error ?? inlineError ?? undefined;
  const hintId = "birthDate-hint";
  const errorId = "birthDate-error";
  const describedBy = [hintId, shownError ? errorId : null].filter(Boolean).join(" ");
  function handleBlur() {
    const iso = normalizeBirthDateInput(value);
    if (iso) onChange(formatBirthDateDisplayInput(iso));
  }
  return (
    <FieldWrapper>
      <label
        htmlFor="birthDate"
        className="block text-body-small font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.birthDate}
      </label>
      <FieldHint
        language={language}
        hi="Sahi janm tithi se aapke graha sahi read hote hain"
        en="The exact date reveals the true alignment of the stars"
      />
      <input
        id="birthDate"
        type="text"
        inputMode="numeric"
        pattern="[0-9/.-]*"
        required
        value={value}
        onChange={(e) => onChange(formatBirthDateDisplayInput(e.target.value))}
        onFocus={onFocus}
        onBlur={handleBlur}
        placeholder="dd/mm/yyyy"
        maxLength={10}
        autoComplete="bday"
        autoCorrect="off"
        enterKeyHint="next"
        aria-invalid={Boolean(shownError)}
        aria-describedby={describedBy}
        className="input-sacred w-full rounded-lg px-4 py-3 text-body"
        style={{ color: "var(--ish-dark)" }}
      />
      <p id={hintId} className="mt-1.5 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
        {language === "hi"
          ? "Format dd/mm/yyyy, jaise 15/08/1992."
          : "Use dd/mm/yyyy, e.g. 15/08/1992."}
      </p>
      <FieldError id={errorId} message={shownError} />
    </FieldWrapper>
  );
});

// --- BirthTimeField ---

interface BirthTimeFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  language: KundaliLang;
  t: KundaliTranslations;
  required?: boolean;
  error?: string;
}

export const BirthTimeField = memo(function BirthTimeField({
  value,
  onChange,
  onFocus,
  language,
  t,
  required = false,
  error,
}: BirthTimeFieldProps) {
  return (
    <FieldWrapper>
      <label
        htmlFor="birthTime"
        className="block text-body-small font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.birthTime}{" "}
        {!required && (
          <span className="font-normal" style={{ color: "var(--ish-text-faint)" }}>
            {t.optional}
          </span>
        )}
      </label>
      <FieldHint
        language={language}
        hi="Janm samay se aapka Lagna aur dasha sahi read hote hain"
        en="Your birth time fixes your Lagna and dasha timeline"
      />
      <input
        id="birthTime"
        type="time"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "birthTime-error" : undefined}
        className="input-sacred w-full rounded-lg px-4 py-3 text-body"
        style={{ color: "var(--ish-dark)" }}
      />
      <FieldError id="birthTime-error" message={error} />
      {!required && (
        <BirthTimeUnknownHelper toggle={t.timeUnknownToggle} explainer={t.timeUnknownExplainer} />
      )}
    </FieldWrapper>
  );
});

function BirthTimeUnknownHelper({ toggle, explainer }: { toggle: string; explainer: string }) {
  // Inline honesty disclosure (rule 32 dead-click + rule 22 voice): a timeless
  // entry gets only a reduced, time-insensitive reading; it never promises
  // Lagna, houses, divisional charts, or dasha timing the chart cannot support.
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-[11px] font-semibold"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "var(--intent-brand-text)",
          textDecoration: "underline dotted",
        }}
      >
        {toggle}
      </button>
      {open && (
        <p className="mt-1.5 text-[11px] leading-snug" style={{ color: "var(--ish-text-muted)" }}>
          {explainer}
        </p>
      )}
    </div>
  );
}

// --- BirthPlaceField ---

interface BirthPlaceFieldProps {
  value: string;
  onChange: (v: string) => void;
  onSelect: (place: PlaceResult) => void;
  onFocus: () => void;
  language: KundaliLang;
  t: KundaliTranslations;
  error?: string;
  /** The committed city (tapped OR confidently resolved). Chip shows when set. */
  selectedPlace: PlaceResult | null;
  /** Clear the committed city and re-open the search (the chip's "change"). */
  onClearPlace: () => void;
  /** "Did you mean …?" suggestion for an ambiguous typed city, or null. */
  ambiguousSuggestion: PlaceResult | null;
}

export const BirthPlaceField = memo(function BirthPlaceField({
  value,
  onChange,
  onSelect,
  onFocus,
  language,
  t,
  error,
  selectedPlace,
  onClearPlace,
  ambiguousSuggestion,
}: BirthPlaceFieldProps) {
  const hintId = "birthPlace-hint";
  const errorId = "birthPlace-error";
  const describedBy = [hintId, error ? errorId : null].filter(Boolean).join(" ");
  return (
    <FieldWrapper>
      <label
        htmlFor="birthPlace"
        className="block text-body-small font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.birthPlace}
      </label>
      {!selectedPlace && (
        <FieldHint
          language={language}
          hi="City se aapke chart ke exact coordinates aur timezone milte hain"
          en="Location gives us the exact coordinates and timezone for your chart"
        />
      )}
      {selectedPlace ? (
        // Committed city → unmistakable chip with one-tap correction. The
        // autocomplete input is hidden; the chip IS the placeSelected=true state.
        <div>
          <PlaceChip place={selectedPlace} onChange={onClearPlace} changeLabel={t.placeChange} />
          <PlaceCalculationDetails place={selectedPlace} language={language} />
        </div>
      ) : (
        <div onFocus={onFocus}>
          <PlaceAutocomplete
            id="birthPlace"
            value={value}
            onChange={onChange}
            onSelect={onSelect}
            placeholder={t.placePlaceholder}
            invalid={Boolean(error)}
            describedBy={describedBy}
          />
          {/* Type-and-submit affordance: typed cities (English OR Hindi script —
              मुंबई resolves via the transliteration table) auto-lock on submit,
              so the helper must not imply a dropdown tap is required. The one
              Devanagari word is a capability EXAMPLE for Hindi typers, not
              register copy (the surrounding copy stays Hinglish, rule 21). */}
          <p id={hintId} className="mt-1.5 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
            {language === "hi"
              ? "City ka naam type karke seedha submit karein — sahi naam khud lock ho jayega. Hindi mein likha naam (jaise मुंबई) bhi chalega."
              : "Type your city and submit — exact names lock automatically. You can also tap a suggestion."}
          </p>
        </div>
      )}
      {!selectedPlace && ambiguousSuggestion && (
        <PlaceDidYouMean
          suggestion={ambiguousSuggestion}
          onAccept={onSelect}
          prefixLabel={t.placeDidYouMean}
        />
      )}
      <FieldError id={errorId} message={error} />
    </FieldWrapper>
  );
});

export function PlaceCalculationDetails({
  place,
  language,
}: {
  place: PlaceResult;
  language: KundaliLang;
}) {
  const coordinates = `${place.latitude.toFixed(4)}°, ${place.longitude.toFixed(4)}°`;
  const labels =
    language === "hi"
      ? {
          summary: "Ganana details",
          coordinates: "Coordinates",
          timezone: "Timezone",
          method: "Kundali method",
        }
      : {
          summary: "Calculation details",
          coordinates: "Coordinates",
          timezone: "Timezone",
          method: "Chart method",
        };

  return (
    <details
      className="mt-2 rounded-lg border border-border-default px-3 py-2 text-caption"
      data-calculation-details
    >
      <summary className="min-h-8 cursor-pointer py-1 font-semibold text-text-muted">
        {labels.summary}
      </summary>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pb-1 text-text-muted">
        <dt>{labels.coordinates}</dt>
        <dd className="text-right text-text-primary">{coordinates}</dd>
        <dt>{labels.timezone}</dt>
        <dd className="text-right text-text-primary">{place.timezone}</dd>
        <dt>{labels.method}</dt>
        <dd className="text-right text-text-primary">Lahiri ayanamsa · Whole-sign houses</dd>
      </dl>
    </details>
  );
}
